import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn, mapFictoanBadgeProps } from "@/lib/utils";
import { FictoanBadgeProps } from "@/lib/fictoan-schema";

const badgeVariants = cva(
    "setu:inline-flex setu:items-center setu:rounded-full setu:!border setu:px-2.5 setu:py-0.5 setu:text-xs setu:font-semibold setu:transition-colors setu:focus:outline-hidden setu:focus:ring-2 setu:focus:ring-ring setu:focus:ring-offset-2",
    {
        variants: {
            variant: {
                default:
                    "setu:!border-transparent setu:bg-primary setu:text-primary-foreground setu:hover:bg-primary/80",
                secondary:
                    "setu:!border-transparent setu:bg-secondary setu:text-secondary-foreground setu:hover:bg-secondary/80",
                destructive:
                    "setu:!border-transparent setu:bg-destructive setu:text-destructive-foreground setu:hover:bg-destructive/80",
                outline: "setu:text-foreground",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
);

// Combined interface for both shadcn/ui and Fictoan props
export interface BadgeProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof badgeVariants>,
        Partial<FictoanBadgeProps> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
    (
        {
            className,
            variant,
            children,
            // Extract Fictoan props
            size,
            shape,
            bgColor,
            bgColour,
            textColor,
            textColour,
            borderColor,
            borderColour,
            padding,
            margin,
            shadow,
            classNames,
            ...props
        },
        ref
    ) => {
        // Map Fictoan props using our standardized mapping
        const { className: mappedClassName } = mapFictoanBadgeProps({
            size,
            shape,
            bgColor,
            bgColour,
            textColor,
            textColour,
            borderColor,
            borderColour,
            padding,
            margin,
            shadow,
            classNames,
            children,
        });

        return (
            <div ref={ref} className={cn(badgeVariants({ variant }), mappedClassName, className)} {...props}>
                {children}
            </div>
        );
    }
);
Badge.displayName = "Badge";

// Fictoan compatibility wrapper
const FictoanBadge = React.forwardRef<HTMLDivElement, FictoanBadgeProps>((props, ref) => {
    const { className, children } = mapFictoanBadgeProps(props);
    return (
        <Badge ref={ref} className={className}>
            {children}
        </Badge>
    );
});
FictoanBadge.displayName = "FictoanBadge";

export { Badge, badgeVariants, FictoanBadge };
